import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI } from '../services/api';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: Request, 2: Reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

// In handleRequestReset function
const handleRequestReset = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  setSuccess('');

  try {
    const result = await authAPI.forgotPassword(email);
    
    if (result.success) {
      if (result.is_temporary) {
        // Show temporary password directly
        setSuccess(`${result.message}`);
        // Optional: auto-fill the reset form
        setResetToken('temp');
        setStep(2);
      } else if (result.reset_token) {
        setResetToken(result.reset_token);
        setSuccess('Reset instructions sent! Check the token below.');
        setStep(2);
      } else {
        setSuccess(result.message);
      }
    } else {
      setError(result.message || 'Failed to send reset instructions');
    }
  } catch (err) {
    setError('An error occurred. Please try again.');
    console.error('Forgot password error:', err);
  } finally {
    setLoading(false);
  }
};

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const result = await authAPI.resetPassword(resetToken, newPassword);
      
      if (result.success) {
        setSuccess('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(result.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <motion.div 
          className="auth-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="auth-header">
            <div className="auth-logo">
              <img src="/images/logo.png" alt="NLNG RA Club" />
              <h2>NLNG RA CLUB</h2>
              <p>Password Recovery</p>
            </div>
          </div>

          <div className="auth-body">
            {error && (
              <motion.div 
                className="alert alert-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                ⚠️ {error}
              </motion.div>
            )}

            {success && (
              <motion.div 
                className="alert alert-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                ✅ {success}
              </motion.div>
            )}

            {step === 1 ? (
              <form onSubmit={handleRequestReset} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email">Enter your email, registration ID, or membership number</label>
                  <div className="input-with-icon">
                    <span className="input-icon">📧</span>
                    <input
                      type="text"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email, registration ID, or membership number"
                      required
                      disabled={loading}
                    />
                  </div>
                  <small className="help-text">
                    Enter the email, registration ID, or membership number associated with your account
                  </small>
                </div>

                <motion.button 
                  type="submit" 
                  className="btn btn-primary btn-block"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">📨</span>
                      <span>Send Reset Instructions</span>
                    </>
                  )}
                </motion.button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="auth-form">
                <div className="form-group">
                  <label htmlFor="resetToken">Reset Token</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔑</span>
                    <input
                      type="text"
                      id="resetToken"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      placeholder="Enter the reset token sent to you"
                      required
                      disabled={loading}
                    />
                  </div>
                  <small className="help-text">
                    Copy the reset token from above or check your email
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min. 6 characters)"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <motion.button 
                  type="submit" 
                  className="btn btn-primary btn-block"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Resetting...</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">🔄</span>
                      <span>Reset Password</span>
                    </>
                  )}
                </motion.button>

                <button 
                  type="button" 
                  className="btn btn-outline btn-block"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  ← Back
                </button>
              </form>
            )}

            <div className="auth-links">
              <p>Remember your password? <Link to="/login">Login here</Link></p>
              <p>Don't have an account? <Link to="/register">Register here</Link></p>
            </div>
          </div>

          <div className="auth-footer">
            <p className="note">
              <span className="note-icon">ℹ️</span>
              For security reasons, reset tokens expire after 1 hour.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;